/**
 * Story Editor Store (spec 010)
 *
 * Zustand store with Immer for the story graph editor. The store is the
 * source of truth for the graph: every user change (move, connect, delete,
 * set start, change condition) is a store action, React Flow re-renders from
 * it, and live validation (`validateGraph`, Shared) is recomputed after every
 * graph or choice change.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { Draft } from 'immer';
import type {
  StoryApiResponse,
  StoryGraph,
  StoryGraphNode,
  StoryGraphEdge,
  StorySceneWithScene,
  ChoiceOption,
  PictureMap,
  SlotUse,
} from '../../../Shared/src/@types';
import { enableMapSet } from 'immer';
import { apiClient } from '../api/client';
import { registerSessionReset } from '../api/session';
import {
  validateGraph,
  type GraphScene,
  type GraphValidationResult,
} from '../../../Shared/src/utils/validateGraph';
import {
  NODE_SPACING_X,
  nextNodePosition,
  dropNodes,
  availableChoicesFrom,
  connectRule,
  edgeConditionRule,
  CONNECT_MESSAGES,
  type ConnectRefusal,
} from '../../../Shared/src/storyOperations/graphRules';

import type { OperationStep } from '../../../Shared/src/operations/types';
import { pictureSlotLabel } from '../../../Shared/src/pictures';
import type {
  CreatedStoryScene,
  StoryOperationError,
  StoryRevert,
} from '../../../Shared/src/storyOperations/types';
// The same place as 039's picture operations see it (a background use by its state).
import { sameUse } from '../../../Shared/src/storyOperations/pictureOperations';

// The placement rules live in Shared (spec 023); the store re-exports them.
export { NODE_SPACING_X, nextNodePosition };

// Enable Immer's MapSet plugin for using Map/Set in state
enableMapSet();

// =============================================================================
// TYPES
// =============================================================================

export type GraphValidation = Omit<GraphValidationResult, 'danglingEdgeIds'>;

/** The part of a story a manual edit changes: its name and its graph. */
export interface StorySnapshot {
  name: string;
  graph: StoryGraph;
}

/** One manual command on the map (spec 010 D-4): undone and redone on the client. */
export interface StoryEditEntry {
  kind: 'edit';
  before: StorySnapshot;
  after: StorySnapshot;
}

/**
 * One applied batch of story operations (spec 023): undone by the server-side
 * revert, redone by posting its recorded steps to the apply route again.
 */
export interface StoryBatchEntry {
  kind: 'batch';
  steps: OperationStep[];
  revert: StoryRevert;
  summaries: string[];
  createdStoryScenes: CreatedStoryScene[];
  /**
   * The story assistant turn that applied this batch (spec 024), kept across
   * undo, redo and remapping, so the turn's message can tell when its batch
   * is the top of `history.past` or `history.future`.
   */
  turnId?: number;
}

/**
 * One distinct user edit in the story history (spec 010 D-3): a manual
 * command, or an assistant batch (spec 023). `undo()` / `redo()` dispatch on
 * `kind`.
 */
export type StoryHistoryEntry = StoryEditEntry | StoryBatchEntry;

/** What `applyOperations` resolves to (spec 023). */
export type ApplyOperationsResult =
  | { ok: true; summaries: string[]; createdSceneIds: number[]; changedSceneIds: number[] }
  | { ok: false; failedIndex?: number; errors: StoryOperationError[]; reason: 'failed' | 'network' };

/** What `fillPicture` resolves to (spec 026): on success, the number of places the picture went into. */
export type FillPictureResult =
  | { ok: true; places: number; label: string }
  | { ok: false; reason: 'failed' | 'network'; errors: StoryOperationError[] };

const EMPTY_PICTURE_MAP: PictureMap = { slots: [] };
const pictureMapOf = (story: Pick<StoryApiResponse, 'pictureMap'> | null | undefined): PictureMap =>
  story && story.pictureMap && Array.isArray(story.pictureMap.slots) ? story.pictureMap : EMPTY_PICTURE_MAP;


export const BATCH_UNDO_CHANGED_NOTICE = "Something changed since, so this can't be undone in one step.";
export const BATCH_REDO_FAILED_NOTICE = "This can't be redone now.";
export const BATCH_NETWORK_NOTICE = 'Could not reach the server; try again.';

export interface StoryHistory {
  past: StoryHistoryEntry[];
  future: StoryHistoryEntry[];
}

/** The story history keeps at most this many entries, oldest dropped first (D-5). */
export const STORY_HISTORY_LIMIT = 100;

export type ConnectResult =
  | { ok: true; edgeId: string }
  | { ok: false; reason: ConnectRefusal; message: string };

interface StoryState {
  story: StoryApiResponse | null;
  isDirty: boolean;
  isLoading: boolean;
  error: string | null;

  /** The name as last loaded or saved; an emptied name reverts to it. */
  savedName: string;
  /** A save request is in flight. */
  isSaving: boolean;
  /** The last save failed; a retry is scheduled and the story stays dirty. */
  saveFailed: boolean;

  /** Short inline message for a refused action (connect, link). */
  notice: string | null;

  /**
   * The story runner preview plays (spec 009): the edge panel is closed and
   * graph editing (connect, delete, set start) is off until it stops.
   */
  previewRunnerActive: boolean;

  // Graph selection
  selectedNodeIds: string[];
  selectedEdgeIds: string[];

  /** Live validation of the graph (spec 010), recomputed on every change. */
  validation: GraphValidation;

  // Edges that cannot be followed (from live validation, or a publish response)
  danglingEdgeIds: Set<string>;

  // StorySceneIds that have choices defined
  choiceStorySceneIds: Set<number>;

  // Choices per storySceneId: the one source for node badges, picker and edge panel
  sceneChoicesMap: Map<number, ChoiceOption[]>;

  /**
   * The story history (spec 010): editor UI state, never saved, empty after
   * every `loadStory`. Newest entry last in `past`; next redo last in `future`.
   */
  history: StoryHistory;
  /** `history.past.length > 0`. */
  canUndo: boolean;
  /** `history.future.length > 0`. */
  canRedo: boolean;
  /**
   * An apply, a batch undo or a batch redo is in flight (spec 023): the map
   * is read-only and further undo / redo calls are ignored.
   */
  batchPending: boolean;

  /**
   * The story's picture map (spec 026), as the last story response carried
   * it (load, save, batch, revert, `refreshPictureMap`). `{slots: []}` when
   * there is none.
   */
  pictureMap: PictureMap;
}

interface StoryActions {
  // Data operations
  loadStory: (id: number) => Promise<void>;
  /** Saves pending changes. Resolves true when nothing is left unsaved. */
  saveStory: () => Promise<boolean>;
  reset: () => void;

  // Story fields
  updateName: (name: string) => void;
  /** On blur: trims the name; an empty name reverts to the last saved one. */
  commitName: () => void;
  updateFolder: (folderId: number | null) => void;

  // Graph node operations
  addNode: (node: StoryGraphNode) => void;
  /** Places a node for a story scene in free space right of the right-most node. */
  addSceneNode: (storySceneId: number) => string | null;
  updateNodePosition: (nodeId: string, position: { x: number; y: number }) => void;
  removeNode: (nodeId: string) => void;
  removeNodes: (nodeIds: string[]) => void;
  setStartNode: (nodeId: string) => void;

  // Graph edge operations
  /** Creates an edge under the per-node rules; `condition` is a choice id. */
  connect: (from: string, to: string, condition?: string) => ConnectResult;
  /** Sets (choice id) or clears (null) an edge's condition under the per-node rules. */
  setEdgeCondition: (edgeId: string, condition: string | null) => boolean;
  addEdge: (edge: StoryGraphEdge) => void;
  updateEdge: (edgeId: string, updates: Partial<StoryGraphEdge>) => void;
  removeEdge: (edgeId: string) => void;

  // Bulk graph update
  setGraph: (graph: StoryGraph) => void;

  // StoryScene link operations
  addSceneReference: (sceneId: number) => Promise<StorySceneWithScene | null>;
  copySceneToStory: (sceneId: number) => Promise<StorySceneWithScene | null>;
  ownScene: (sceneId: number) => Promise<StorySceneWithScene | null>;
  /** "+ New scene": creates `Scene N`, owns it and places its node. */
  createNewScene: () => Promise<StorySceneWithScene | null>;
  removeStoryScene: (storySceneId: number) => Promise<boolean>;

  // Selection
  selectNodes: (ids: string[]) => void;
  selectEdges: (ids: string[]) => void;
  clearSelection: () => void;

  // Utility
  markDirty: () => void;
  setNotice: (notice: string | null) => void;
  setPreviewRunnerActive: (active: boolean) => void;

  // Dangling edge highlighting (from a publish response)
  setDanglingEdges: (edgeIds: string[]) => void;

  // Choice-edge synchronisation
  syncChoiceEdges: (storySceneId: number, choices: ChoiceOption[]) => void;

  // Story history (spec 010)
  /** Removes nodes and edges as one entry (one Delete keypress on the canvas). */
  removeElements: (nodeIds: string[], edgeIds: string[]) => void;
  /** Gathers the changes until the matching `endHistoryGroup` into one entry; groups nest. */
  beginHistoryGroup: () => void;
  endHistoryGroup: () => void;
  /**
   * Puts an entry on top of `history.past`, clears `future` and drops the
   * oldest past `STORY_HISTORY_LIMIT`. The one way any producer (manual
   * commands here, assistant batches in spec 023) adds to the history.
   */
  pushHistoryEntry: (entry: StoryHistoryEntry) => void;
  /** Undoes the top of `past` (an edit entry on the client, D-4). */
  undo: () => Promise<void>;
  /** Redoes the top of `future`. */
  redo: () => Promise<void>;

  /**
   * Applies a batch of story operations on the server (spec 023): saves
   * first, adopts the returned story and pushes one batch entry.
   */
  applyOperations: (steps: OperationStep[], opts?: { turnId?: number }) => Promise<ApplyOperationsResult>;

  /** Re-reads the story (`GET /stories/:id`) and adopts only its picture map (spec 026). Resolves true on success. */
  refreshPictureMap: () => Promise<boolean>;
  /**
   * Fills a picture of the map (spec 026): loads the image's natural size and
   * applies one `fillPictureSlot` step with `applyOperations` (one story
   * history entry; changed open scenes reload). `uses` limits it to those
   * places (027). Resolves with the number of places on success.
   */
  fillPicture: (slotId: string, mediaId: number, opts?: { uses?: SlotUse[] }) => Promise<FillPictureResult>;
  /**
   * Takes these pictures out of the story (spec 027 D-30): one
   * `clearPictures` step with `applyOperations` (one story history entry, so
   * one `undo()` puts them all back). The media stay in the library.
   */
  discardPictures: (mediaIds: number[]) => Promise<ApplyOperationsResult>;
}

type StoryStore = StoryState & StoryActions;

// =============================================================================
// PURE HELPERS
// =============================================================================

/** `Scene N` with the smallest N no scene of the story already uses. */
export function nextSceneName(existingNames: Array<string | null | undefined>): string {
  const taken = new Set(existingNames.map((n) => (n ?? '').trim()));
  let n = 1;
  while (taken.has(`Scene ${n}`)) n += 1;
  return `Scene ${n}`;
}

function newEdgeId(): string {
  const uuid =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `edge-${uuid}`;
}

function newNodeId(storySceneId: number): string {
  return `node-${storySceneId}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** The scenes of the story as the validator sees them (choices from the map). */
export function graphScenesOf(
  story: Pick<StoryApiResponse, 'storyScenes'>,
  sceneChoicesMap: Map<number, ChoiceOption[]>,
): Record<number, GraphScene> {
  const out: Record<number, GraphScene> = {};
  for (const ss of story.storyScenes) {
    out[ss.id] = {
      name: ss.sceneName || `Scene #${ss.sceneId}`,
      choices: sceneChoicesMap.get(ss.id) ?? [],
    };
  }
  return out;
}

/** Choices of the node's scene that have no edge from that node yet. */
export function availableChoices(
  state: Pick<StoryState, 'story' | 'sceneChoicesMap'>,
  nodeId: string,
): ChoiceOption[] {
  const story = state.story;
  if (!story) return [];
  const node = story.graph.nodes.find((n) => n.id === nodeId);
  if (!node) return [];
  return availableChoicesFrom(story.graph.edges, nodeId, state.sceneChoicesMap.get(node.storySceneId) ?? []);
}

const emptyValidation = (): GraphValidation => ({ valid: true, errors: [], warnings: [] });

/** Recompute live validation inside a draft. */
function revalidate(s: Draft<StoryState>) {
  if (!s.story) {
    s.validation = emptyValidation();
    s.danglingEdgeIds = new Set();
    return;
  }
  const result = validateGraph(
    s.story.graph as StoryGraph,
    graphScenesOf(s.story as StoryApiResponse, s.sceneChoicesMap as Map<number, ChoiceOption[]>),
  );
  s.validation = { valid: result.valid, errors: result.errors, warnings: result.warnings };
  s.danglingEdgeIds = new Set(result.danglingEdgeIds);
}

/** Rebuild the choice caches from a server copy of the story. */
function adoptChoices(s: Draft<StoryState>, storyScenes: StorySceneWithScene[]) {
  s.choiceStorySceneIds = new Set();
  s.sceneChoicesMap = new Map();
  for (const ss of storyScenes) {
    if (ss.choices && ss.choices.length > 0) {
      s.choiceStorySceneIds.add(ss.id);
      s.sceneChoicesMap.set(ss.id, ss.choices.map((c) => ({ ...c })));
    }
  }
}

/**
 * The graph as undo or redo puts it back (spec 010): nodes whose scene is no
 * longer in the story are left out with their edges (the start moves as
 * `dropNodes` does), and conditional edge labels mirror the current choices.
 * Returns the input itself when nothing needed changing.
 */
export function restoredGraph(
  graph: StoryGraph,
  storySceneIds: Set<number>,
  sceneChoicesMap: Map<number, ChoiceOption[]>,
): StoryGraph {
  const gone = new Set(graph.nodes.filter((n) => !storySceneIds.has(n.storySceneId)).map((n) => n.id));
  const nodes = gone.size ? graph.nodes.filter((n) => !gone.has(n.id)) : graph.nodes;
  const sceneOf = new Map(nodes.map((n) => [n.id, n.storySceneId]));
  let changed = gone.size > 0;
  const edges: StoryGraphEdge[] = [];
  for (const e of graph.edges) {
    if (gone.has(e.from) || gone.has(e.to)) continue;
    const ssId = sceneOf.get(e.from);
    const choice = e.condition && ssId !== undefined
      ? (sceneChoicesMap.get(ssId) ?? []).find((c) => c.id === e.condition)
      : undefined;
    if (choice && e.label !== choice.text) {
      edges.push({ ...e, label: choice.text });
      changed = true;
    } else {
      edges.push(e);
    }
  }
  if (!changed) return graph;
  const startNodeId = nodes.some((n) => n.id === graph.startNodeId) ? graph.startNodeId : nodes[0]?.id ?? '';
  return { ...graph, nodes, edges, startNodeId };
}

/** `ss-<old>` → `ss-<new>` in every string of a step's args (spec 023 redo remap). */
function remapKeys(value: unknown, keys: Map<string, string>): unknown {
  if (typeof value === 'string') return keys.get(value) ?? value;
  if (Array.isArray(value)) {
    const out = value.map((v) => remapKeys(v, keys));
    return out.some((v, i) => v !== value[i]) ? out : value;
  }
  if (value && typeof value === 'object') {
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = remapKeys(v, keys);
      if (out[k] !== v) changed = true;
    }
    return changed ? out : value;
  }
  return value;
}

function remapGraph(graph: StoryGraph, ids: Map<number, number>): StoryGraph {
  if (!graph.nodes.some((n) => ids.has(n.storySceneId))) return graph;
  return {
    ...graph,
    nodes: graph.nodes.map((n) => (ids.has(n.storySceneId) ? { ...n, storySceneId: ids.get(n.storySceneId)! } : n)),
  };
}

/**
 * A future entry rewritten for the rows a redone batch created (spec 023):
 * node `storySceneId`s in edit snapshots and `ss-<id>` keys in batch steps.
 */
export function remapHistoryEntry(entry: StoryHistoryEntry, ids: Map<number, number>): StoryHistoryEntry {
  if (ids.size === 0) return entry;
  if (entry.kind === 'edit') {
    const before = remapGraph(entry.before.graph, ids);
    const after = remapGraph(entry.after.graph, ids);
    if (before === entry.before.graph && after === entry.after.graph) return entry;
    return { ...entry, before: { ...entry.before, graph: before }, after: { ...entry.after, graph: after } };
  }
  const keys = new Map([...ids].map(([from, to]) => [`ss-${from}`, `ss-${to}`]));
  const steps = remapKeys(entry.steps, keys) as OperationStep[];
  return steps === entry.steps ? entry : { ...entry, steps };
}

/** Lets the scene editor catch up after a batch (spec 023): reload a changed scene, drop a deleted one. */
async function refreshOpenScene(changed: number[], deleted: number[]) {
  if (changed.length === 0 && deleted.length === 0) return;
  const { useSceneStore } = await import('./sceneStore');
  const sc = useSceneStore.getState();
  const id = sc.scene?.id as number | undefined;
  if (id === undefined || id === null) return;
  if (deleted.includes(id)) {
    // The tab closes (StoryEditor); nothing of a deleted scene is saved.
    sc.markClean();
  } else if (changed.includes(id)) {
    await sc.loadScene(id);
  }
}

/** The scene editor's pending edits go to the server before a batch reads the scenes. */
async function flushOpenScene() {
  const { useSceneStore } = await import('./sceneStore');
  const sc = useSceneStore.getState();
  if (sc.scene && sc.isDirty) await sc.saveScene();
}

const sameChoices = (a: ChoiceOption[] | undefined, b: ChoiceOption[]) =>
  (a ?? []).length === b.length &&
  (a ?? []).every((c, i) => c.id === b[i].id && c.text === b[i].text);

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: StoryState = {
  story: null,
  isDirty: false,
  isLoading: false,
  error: null,
  savedName: '',
  isSaving: false,
  saveFailed: false,
  notice: null,
  previewRunnerActive: false,
  selectedNodeIds: [],
  selectedEdgeIds: [],
  validation: emptyValidation(),
  danglingEdgeIds: new Set(),
  choiceStorySceneIds: new Set(),
  sceneChoicesMap: new Map(),
  history: { past: [], future: [] },
  canUndo: false,
  canRedo: false,
  batchPending: false,
  pictureMap: EMPTY_PICTURE_MAP,
};

// =============================================================================
// DEBOUNCE + SINGLE IN-FLIGHT SAVE
// =============================================================================

export const SAVE_DEBOUNCE_MS = 2000;
export const SAVE_RETRY_MS = 5000;

let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<boolean> | null = null;

function debouncedSave(fn: () => Promise<unknown>, delay = SAVE_DEBOUNCE_MS) {
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    saveTimeout = null;
    void fn();
  }, delay);
}

function cancelDebouncedSave() {
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = null;
}

// =============================================================================
// STORE
// =============================================================================

export const useStoryStore = create<StoryStore>()(
  subscribeWithSelector(
    immer((set, get) => {
      // ── Story history (spec 010) ───────────────────────────────────────
      // A group gathers several changes into one entry, from the snapshot at
      // the outermost begin to the state at the outermost end.
      let groupDepth = 0;
      let groupStart: StorySnapshot | null = null;
      /** The name before the typing that `commitName` will record (null: not typing). */
      let nameEditStart: string | null = null;

      const snapshot = (): StorySnapshot | null => {
        const story = get().story;
        return story ? { name: story.name, graph: story.graph } : null;
      };

      const sameSnapshot = (a: StorySnapshot, b: StorySnapshot) => a.name === b.name && a.graph === b.graph;

      const setHistory = (s: Draft<StoryState>, history: StoryHistory) => {
        s.history = history as Draft<StoryHistory>;
        s.canUndo = history.past.length > 0;
        s.canRedo = history.future.length > 0;
      };

      const clearHistory = (s: Draft<StoryState>) => {
        groupDepth = 0;
        groupStart = null;
        nameEditStart = null;
        setHistory(s, { past: [], future: [] });
      };

      const pushEntry = (entry: StoryHistoryEntry) => {
        set((s) => {
          const past = [...(s.history.past as StoryHistoryEntry[]), entry];
          if (past.length > STORY_HISTORY_LIMIT) past.splice(0, past.length - STORY_HISTORY_LIMIT);
          setHistory(s, { past, future: [] });
        });
      };

      /** Records one edit entry from `before` to now, unless nothing changed or a group is open. */
      const recordEdit = (before: StorySnapshot | null) => {
        if (groupDepth > 0 || !before) return;
        const after = snapshot();
        if (!after || sameSnapshot(before, after)) return;
        pushEntry({ kind: 'edit', before, after });
      };

      /** Apply a graph mutation, mark dirty, revalidate and schedule a save. */
      const mutateGraph = (
        fn: (graph: Draft<StoryGraph>, s: Draft<StoryState>) => boolean | void,
        opts: { record?: boolean } = {},
      ) => {
        // The map is read-only while a batch is in flight (spec 023).
        if (get().batchPending) return false;
        const before = snapshot();
        let changed = false;
        set((s) => {
          if (!s.story) return;
          if (fn(s.story.graph, s) === false) return;
          changed = true;
          s.isDirty = true;
          revalidate(s);
        });
        if (changed) {
          if (opts.record !== false) recordEdit(before);
          debouncedSave(() => get().saveStory());
        }
        return changed;
      };

      /** Puts a snapshot back (undo / redo of an edit entry, D-4) and saves it normally. */
      const restore = (target: StorySnapshot) => {
        nameEditStart = null;
        set((s) => {
          if (!s.story) return;
          const ids = new Set(s.story.storyScenes.map((ss) => ss.id));
          const graph = restoredGraph(target.graph, ids, s.sceneChoicesMap as Map<number, ChoiceOption[]>);
          s.story.name = target.name;
          s.story.graph = graph as Draft<StoryGraph>;
          const nodeIds = new Set(graph.nodes.map((n) => n.id));
          const edgeIds = new Set(graph.edges.map((e) => e.id));
          s.selectedNodeIds = s.selectedNodeIds.filter((id) => nodeIds.has(id));
          s.selectedEdgeIds = s.selectedEdgeIds.filter((id) => edgeIds.has(id));
          s.isDirty = true;
          revalidate(s);
        });
        debouncedSave(() => get().saveStory());
      };

      /** Takes a server copy of the story after a batch, as a load does, keeping the history. */
      const adoptStory = (story: StoryApiResponse) => {
        cancelDebouncedSave();
        nameEditStart = null;
        set((s) => {
          const map = story.pictureMap ? pictureMapOf(story) : (s.pictureMap as PictureMap);
          s.story = { ...story, pictureMap: map };
          s.pictureMap = map;
          s.savedName = story.name;
          s.isDirty = false;
          s.saveFailed = false;
          adoptChoices(s, story.storyScenes ?? []);
          const nodeIds = new Set(story.graph.nodes.map((n) => n.id));
          const edgeIds = new Set(story.graph.edges.map((e) => e.id));
          s.selectedNodeIds = s.selectedNodeIds.filter((id) => nodeIds.has(id));
          s.selectedEdgeIds = s.selectedEdgeIds.filter((id) => edgeIds.has(id));
          revalidate(s);
        });
      };

      const setPending = (pending: boolean) => set((s) => { s.batchPending = pending; });

      const pastOf = () => get().history.past as StoryHistoryEntry[];
      const futureOf = () => get().history.future as StoryHistoryEntry[];
      const writeHistory = (past: StoryHistoryEntry[], future: StoryHistoryEntry[]) =>
        set((s) => setHistory(s, { past, future }));

      /** Undo of the batch entry on top of `past` (spec 023). */
      const undoBatch = async (entry: StoryBatchEntry) => {
        const storyId = get().story!.id;
        if (!(await get().saveStory())) {
          get().setNotice(BATCH_NETWORK_NOTICE);
          return;
        }
        let res;
        try {
          res = await apiClient.revertStoryOperations(storyId, entry.revert);
        } catch {
          get().setNotice(BATCH_NETWORK_NOTICE);
          return;
        }
        if (get().story?.id !== storyId) return;
        if (!res.success) {
          if (res.error === 'changed_since') {
            // The entry and every entry below it could only be reached through it.
            get().setNotice(BATCH_UNDO_CHANGED_NOTICE);
            writeHistory([], futureOf());
          } else {
            get().setNotice(BATCH_NETWORK_NOTICE);
          }
          return;
        }
        adoptStory(res.data.story);
        // The revert restored each scene it wrote to what the batches below
        // left it: every batch entry below that lists the scene now expects
        // the stamp the revert left. A batch in between that does not list the
        // scene passes nothing on, and a later undo of a nearer batch
        // re-rebases the ones below it.
        const stamps = new Map((res.data.stamps?.scenes ?? []).map((x) => [x.sceneId, x.updatedAt]));
        const past = pastOf().slice(0, -1).map((below) => {
          if (below.kind !== 'batch' || !below.revert.scenes.some((sc) => stamps.has(sc.sceneId))) return below;
          const scenes = below.revert.scenes.map((sc) =>
            stamps.has(sc.sceneId) ? { ...sc, updatedAt: stamps.get(sc.sceneId)! } : sc);
          return { ...below, revert: { ...below.revert, scenes } };
        });
        writeHistory(past, [...futureOf(), entry]);
        await refreshOpenScene(entry.revert.scenes.map((sc) => sc.sceneId), entry.revert.createdSceneIds);
      };

      /** Redo of the batch entry on top of `future` (spec 023). */
      const redoBatch = async (entry: StoryBatchEntry) => {
        const storyId = get().story!.id;
        if (!(await get().saveStory())) {
          get().setNotice(BATCH_NETWORK_NOTICE);
          return;
        }
        let res;
        try {
          res = await apiClient.applyStoryOperations(storyId, entry.steps);
        } catch {
          get().setNotice(BATCH_NETWORK_NOTICE);
          return;
        }
        if (get().story?.id !== storyId) return;
        if (!res.success) {
          if (res.error === 'operations_failed') {
            get().setNotice(BATCH_REDO_FAILED_NOTICE);
            writeHistory(pastOf(), []);
          } else {
            get().setNotice(BATCH_NETWORK_NOTICE);
          }
          return;
        }
        const data = res.data;
        adoptStory(data.story);
        // New rows replace the old ones: entries still in `future` follow them.
        const fresh = new Map(data.createdStoryScenes.map((c) => [c.key, c.storySceneId]));
        const ids = new Map<number, number>();
        for (const old of entry.createdStoryScenes) {
          const next = fresh.get(old.key);
          if (next !== undefined && next !== old.storySceneId) ids.set(old.storySceneId, next);
        }
        const redone: StoryBatchEntry = {
          ...entry,
          revert: data.revert,
          summaries: data.summaries,
          createdStoryScenes: data.createdStoryScenes,
        };
        const future = futureOf().slice(0, -1).map((x) => remapHistoryEntry(x, ids));
        const past = [...pastOf(), redone];
        if (past.length > STORY_HISTORY_LIMIT) past.splice(0, past.length - STORY_HISTORY_LIMIT);
        writeHistory(past, future);
        await refreshOpenScene(data.changedSceneIds, []);
      };

      const saveOnce = async (): Promise<boolean> => {
        const { story, isDirty } = get();
        if (!story || !isDirty) return true;
        cancelDebouncedSave();
        const snapshot = { id: story.id, name: story.name, folderId: story.folderId, graph: story.graph };
        set((s) => {
          s.isSaving = true;
        });
        let ok = false;
        try {
          const res = await apiClient.updateStory(snapshot.id, {
            name: snapshot.name,
            folderId: snapshot.folderId,
            graph: snapshot.graph,
          });
          ok = !!res.success;
          if (res.success) {
            // Compare the live state (not a draft) with the snapshot that was sent.
            const now = get().story;
            const changedSince =
              !!now &&
              (now.name !== snapshot.name ||
                now.folderId !== snapshot.folderId ||
                now.graph !== snapshot.graph);
            set((s) => {
              s.isSaving = false;
              s.saveFailed = false;
              if (!s.story || s.story.id !== snapshot.id) return;
              s.savedName = res.data.name;
              if (changedSince) {
                // Edits landed while the request was in flight: keep them.
                s.isDirty = true;
                return;
              }
              const scenesBefore = s.story.storyScenes;
              s.isDirty = false;
              // A PATCH never changes the picture map (026); keep it when the response leaves it out.
              const map = res.data.pictureMap ? pictureMapOf(res.data) : (s.pictureMap as PictureMap);
              s.story = { ...res.data, pictureMap: map };
              s.pictureMap = map;
              // The server copy is authoritative for the graph and name. Choices
              // come from the server scenes unless a scene tab synced newer ones.
              const choicesBefore = new Map(s.sceneChoicesMap);
              adoptChoices(s, res.data.storyScenes ?? scenesBefore);
              for (const [id, choices] of choicesBefore) {
                if (s.story.storyScenes.some((ss) => ss.id === id)) {
                  s.sceneChoicesMap.set(id, choices);
                  s.choiceStorySceneIds.add(id);
                }
              }
              revalidate(s);
            });
          }
        } catch (err) {
          console.error('Failed to save story:', err);
          ok = false;
        }
        if (!ok) {
          set((s) => {
            s.isSaving = false;
            s.saveFailed = true;
          });
          debouncedSave(() => get().saveStory(), SAVE_RETRY_MS);
          return false;
        }
        if (get().isDirty) {
          // Newer edits are waiting; send them after the usual debounce.
          debouncedSave(() => get().saveStory());
          return false;
        }
        return true;
      };

      return {
        ...initialState,

        // =======================================================================
        // DATA OPERATIONS
        // =======================================================================

        loadStory: async (id: number) => {
          set((s) => {
            s.isLoading = true;
            s.error = null;
          });
          try {
            const res = await apiClient.getStory(id);
            if (!res.success) throw new Error(res.error || 'Failed to load story');
            set((s) => {
              const map = pictureMapOf(res.data);
              s.story = { ...res.data, pictureMap: map };
              s.pictureMap = map;
              s.savedName = res.data.name;
              s.isLoading = false;
              s.isDirty = false;
              s.saveFailed = false;
              adoptChoices(s, res.data.storyScenes);
              revalidate(s);
              clearHistory(s);
            });
          } catch (err: unknown) {
            set((s) => {
              s.isLoading = false;
              s.error = err instanceof Error ? err.message : String(err);
            });
          }
        },

        saveStory: async () => {
          // One save at a time: a call during a save waits for it, then saves
          // whatever is still dirty.
          while (inFlight) {
            await inFlight;
          }
          const p = saveOnce();
          inFlight = p;
          try {
            return await p;
          } finally {
            if (inFlight === p) inFlight = null;
          }
        },

        reset: () => {
          cancelDebouncedSave();
          inFlight = null;
          groupDepth = 0;
          groupStart = null;
          nameEditStart = null;
          set({
            ...initialState,
            validation: emptyValidation(),
            danglingEdgeIds: new Set(),
            choiceStorySceneIds: new Set(),
            sceneChoicesMap: new Map(),
            history: { past: [], future: [] },
          });
        },

        // =======================================================================
        // STORY FIELDS
        // =======================================================================

        updateName: (name: string) => {
          // Typing records nothing; `commitName` records the whole rename.
          const current = get().story?.name;
          if (nameEditStart === null && current !== undefined && current !== name) nameEditStart = current;
          set((s) => {
            if (s.story) {
              s.story.name = name;
              s.isDirty = true;
            }
          });
          debouncedSave(() => get().saveStory());
        },

        commitName: () => {
          const { story, savedName } = get();
          if (!story) return;
          const trimmed = story.name.trim();
          const next = trimmed || savedName;
          const from = nameEditStart ?? story.name;
          if (next !== story.name) get().updateName(next);
          nameEditStart = null;
          if (next !== from) recordEdit({ name: from, graph: get().story!.graph });
        },

        updateFolder: (folderId: number | null) => {
          set((s) => {
            if (s.story) {
              s.story.folderId = folderId;
              s.isDirty = true;
            }
          });
          debouncedSave(() => get().saveStory());
        },

        // =======================================================================
        // GRAPH NODE OPERATIONS
        // =======================================================================

        addNode: (node: StoryGraphNode) => {
          mutateGraph((g) => {
            g.nodes.push(node);
            if (g.nodes.length === 1 || !g.nodes.some((n) => n.id === g.startNodeId)) {
              g.startNodeId = node.id;
            }
          });
        },

        addSceneNode: (storySceneId: number) => {
          const story = get().story;
          if (!story) return null;
          const existing = story.graph.nodes.find((n) => n.storySceneId === storySceneId);
          if (existing) return existing.id;
          const node: StoryGraphNode = {
            id: newNodeId(storySceneId),
            storySceneId,
            position: nextNodePosition(story.graph.nodes),
            type: 'scene',
          };
          get().addNode(node);
          return node.id;
        },

        updateNodePosition: (nodeId: string, position: { x: number; y: number }) => {
          mutateGraph((g) => {
            const node = g.nodes.find((n) => n.id === nodeId);
            if (!node) return false;
            if (node.position.x === position.x && node.position.y === position.y) return false;
            node.position = { x: position.x, y: position.y };
          });
        },

        removeNode: (nodeId: string) => {
          get().removeNodes([nodeId]);
        },

        removeNodes: (nodeIds: string[]) => {
          const ids = new Set(nodeIds);
          mutateGraph((g, s) => {
            if (!g.nodes.some((n) => ids.has(n.id))) return false;
            dropNodes(g, ids);
            s.selectedNodeIds = s.selectedNodeIds.filter((id) => !ids.has(id));
          });
        },

        setStartNode: (nodeId: string) => {
          mutateGraph((g) => {
            if (!g.nodes.some((n) => n.id === nodeId) || g.startNodeId === nodeId) return false;
            g.startNodeId = nodeId;
          });
        },

        // =======================================================================
        // GRAPH EDGE OPERATIONS
        // =======================================================================

        connect: (from: string, to: string, condition?: string) => {
          const state = get();
          const story = state.story;
          const fail = (reason: Extract<ConnectResult, { ok: false }>['reason'], message: string): ConnectResult => ({
            ok: false,
            reason,
            message,
          });
          if (!story) return fail('missing-node', 'No story is open');
          if (state.batchPending) return fail('missing-node', CONNECT_MESSAGES['missing-node']);
          // The 010 rules, shared with the story operations (spec 023).
          const rule = connectRule(
            story.graph,
            (n) => state.sceneChoicesMap.get(n.storySceneId) ?? [],
            from,
            to,
            condition,
          );
          if (rule.ok === false) return fail(rule.reason, rule.message);

          const edge: StoryGraphEdge = { id: newEdgeId(), from, to };
          if (rule.condition) {
            edge.condition = rule.condition;
            edge.label = rule.label ?? '';
          }
          mutateGraph((g) => {
            g.edges.push(edge);
          });
          return { ok: true, edgeId: edge.id };
        },

        setEdgeCondition: (edgeId: string, condition: string | null) => {
          const state = get();
          const story = state.story;
          if (!story) return false;
          // The 010 rules, shared with the story operations (spec 023).
          const rule = edgeConditionRule(
            story.graph,
            (n) => state.sceneChoicesMap.get(n.storySceneId) ?? [],
            edgeId,
            condition,
          );
          if (rule.ok === false) return false;
          if (rule.condition) {
            const { condition: id, label } = rule;
            return mutateGraph((g) => {
              const e = g.edges.find((x) => x.id === edgeId)!;
              e.condition = id;
              e.label = label;
            });
          }
          return mutateGraph((g) => {
            const e = g.edges.find((x) => x.id === edgeId)!;
            delete e.condition;
            delete e.label;
          });
        },

        addEdge: (edge: StoryGraphEdge) => {
          mutateGraph((g) => {
            g.edges.push(edge);
          });
        },

        updateEdge: (edgeId: string, updates: Partial<StoryGraphEdge>) => {
          mutateGraph((g) => {
            const edge = g.edges.find((e) => e.id === edgeId);
            if (!edge) return false;
            Object.assign(edge, updates);
          });
        },

        removeEdge: (edgeId: string) => {
          mutateGraph((g, s) => {
            if (!g.edges.some((e) => e.id === edgeId)) return false;
            g.edges = g.edges.filter((e) => e.id !== edgeId);
            s.selectedEdgeIds = s.selectedEdgeIds.filter((id) => id !== edgeId);
          });
        },

        // =======================================================================
        // BULK GRAPH UPDATE
        // =======================================================================

        setGraph: (graph: StoryGraph) => {
          mutateGraph((_g, s) => {
            s.story!.graph = graph;
          });
        },

        // =======================================================================
        // STORY SCENE LINK OPERATIONS
        // =======================================================================

        addSceneReference: async (sceneId: number) => linkScene('reference', sceneId),
        copySceneToStory: async (sceneId: number) => linkScene('copy', sceneId),
        ownScene: async (sceneId: number) => linkScene('own', sceneId),

        createNewScene: async () => {
          const { story } = get();
          if (!story) return null;
          const name = nextSceneName(story.storyScenes.map((ss) => ss.sceneName));
          try {
            // POST /scenes seeds the new-scene defaults of spec 004.
            const res = await apiClient.createScene({ name });
            if (!res.success) {
              get().setNotice(res.error || 'Could not create the scene');
              return null;
            }
            const ss = await get().ownScene(res.data.id);
            if (!ss) return null;
            get().addSceneNode(ss.id);
            return ss;
          } catch (err) {
            console.error('Failed to create new scene:', err);
            get().setNotice('Could not create the scene');
            return null;
          }
        },

        removeStoryScene: async (storySceneId: number) => {
          const { story } = get();
          if (!story) return false;
          try {
            const res = await apiClient.removeStoryScene(story.id, storySceneId);
            if (!res.success) return false;
          } catch (err) {
            console.error('Failed to remove story scene:', err);
            return false;
          }
          mutateGraph((g, s) => {
            s.story!.storyScenes = s.story!.storyScenes.filter((ss) => ss.id !== storySceneId);
            s.sceneChoicesMap.delete(storySceneId);
            s.choiceStorySceneIds.delete(storySceneId);
            const removed = new Set(g.nodes.filter((n) => n.storySceneId === storySceneId).map((n) => n.id));
            dropNodes(g, removed);
          }, { record: false });
          return true;
        },

        // =======================================================================
        // SELECTION
        // =======================================================================

        selectNodes: (ids: string[]) => {
          set((s) => {
            s.selectedNodeIds = ids;
            s.selectedEdgeIds = [];
          });
        },

        selectEdges: (ids: string[]) => {
          set((s) => {
            s.selectedEdgeIds = ids;
            s.selectedNodeIds = [];
          });
        },

        clearSelection: () => {
          set((s) => {
            s.selectedNodeIds = [];
            s.selectedEdgeIds = [];
          });
        },

        // =======================================================================
        // UTILITY
        // =======================================================================

        markDirty: () => {
          set((s) => {
            s.isDirty = true;
          });
        },

        setNotice: (notice: string | null) => {
          set((s) => {
            s.notice = notice;
          });
        },

        setPreviewRunnerActive: (active: boolean) => {
          set((s) => {
            s.previewRunnerActive = active;
          });
        },

        setDanglingEdges: (edgeIds: string[]) => {
          set((s) => {
            s.danglingEdgeIds = new Set(edgeIds);
          });
        },

        // =======================================================================
        // CHOICE-EDGE SYNCHRONISATION
        // =======================================================================

        syncChoiceEdges: (storySceneId: number, choices: ChoiceOption[]) => {
          // A no-op when nothing differs: callers run it on every scene load,
          // and a needless update would re-trigger them.
          const current = get();
          if (!current.story) return;
          const nodeIdsNow = new Set(
            current.story.graph.nodes.filter((n) => n.storySceneId === storySceneId).map((n) => n.id),
          );
          const ssNow = current.story.storyScenes.find((x) => x.id === storySceneId);
          const labelsStale = current.story.graph.edges.some((e) => {
            if (!nodeIdsNow.has(e.from) || !e.condition) return false;
            const match = choices.find((c) => c.id === e.condition);
            return !!match && e.label !== match.text;
          });
          if (
            !labelsStale &&
            sameChoices(current.sceneChoicesMap.get(storySceneId), choices) &&
            (!ssNow || sameChoices(ssNow.choices ?? undefined, choices))
          ) {
            // Nothing to sync; only refresh validation if it is out of date.
            const fresh = validateGraph(current.story.graph, graphScenesOf(current.story, current.sceneChoicesMap));
            const sameDangling =
              fresh.danglingEdgeIds.length === current.danglingEdgeIds.size &&
              fresh.danglingEdgeIds.every((id) => current.danglingEdgeIds.has(id));
            const sameErrors = fresh.errors.join('\n') === current.validation.errors.join('\n');
            if (!sameDangling || !sameErrors) set((s) => revalidate(s));
            return;
          }

          let dirty = false;
          set((s) => {
            if (!s.story) return;
            const before = s.sceneChoicesMap.get(storySceneId);
            const choicesChanged = !sameChoices(before as ChoiceOption[] | undefined, choices);

            if (choices.length > 0) {
              s.choiceStorySceneIds.add(storySceneId);
              s.sceneChoicesMap.set(storySceneId, choices.map((c) => ({ ...c })));
            } else {
              s.choiceStorySceneIds.delete(storySceneId);
              s.sceneChoicesMap.delete(storySceneId);
            }
            const ss = s.story.storyScenes.find((x) => x.id === storySceneId);
            if (ss) ss.choices = choices.map((c) => ({ ...c }));

            // Edge labels mirror the current choice text.
            const nodeIds = new Set(
              s.story.graph.nodes.filter((n) => n.storySceneId === storySceneId).map((n) => n.id),
            );
            let labelsChanged = false;
            for (const edge of s.story.graph.edges) {
              if (!nodeIds.has(edge.from) || !edge.condition) continue;
              const match = choices.find((c) => c.id === edge.condition);
              if (match && edge.label !== match.text) {
                edge.label = match.text;
                labelsChanged = true;
              }
            }

            revalidate(s);
            if (labelsChanged || (choicesChanged && nodeIds.size > 0)) {
              s.isDirty = true;
              dirty = true;
            }
          });
          if (dirty) debouncedSave(() => get().saveStory());
        },

        // =======================================================================
        // STORY HISTORY (spec 010)
        // =======================================================================

        removeElements: (nodeIds: string[], edgeIds: string[]) => {
          const st = get();
          st.beginHistoryGroup();
          try {
            if (nodeIds.length > 0) st.removeNodes(nodeIds);
            for (const id of edgeIds) st.removeEdge(id);
          } finally {
            st.endHistoryGroup();
          }
        },

        beginHistoryGroup: () => {
          if (groupDepth++ > 0) return;
          groupStart = snapshot();
        },

        endHistoryGroup: () => {
          if (groupDepth === 0) return;
          if (--groupDepth > 0) return;
          const start = groupStart;
          groupStart = null;
          recordEdit(start);
        },

        pushHistoryEntry: (entry: StoryHistoryEntry) => {
          pushEntry(entry);
        },

        undo: async () => {
          const { history, story, batchPending } = get();
          const entry = history.past[history.past.length - 1];
          if (!entry || !story || groupDepth > 0 || batchPending) return;
          switch (entry.kind) {
            case 'edit':
              restore(entry.before);
              break;
            case 'batch':
              setPending(true);
              try {
                await undoBatch(entry);
              } finally {
                setPending(false);
              }
              return;
          }
          set((s) => {
            setHistory(s, {
              past: (s.history.past as StoryHistoryEntry[]).slice(0, -1),
              future: [...(s.history.future as StoryHistoryEntry[]), entry],
            });
          });
        },

        applyOperations: (steps: OperationStep[], opts?: { turnId?: number }) => applyOperations(steps, opts),

        refreshPictureMap: async () => {
          const story = get().story;
          if (!story) return false;
          let res;
          try {
            res = await apiClient.getStory(story.id);
          } catch {
            return false;
          }
          if (!res.success || get().story?.id !== story.id) return false;
          const map = pictureMapOf(res.data);
          set((s) => {
            if (!s.story) return;
            s.story.pictureMap = map;
            s.pictureMap = map;
          });
          return true;
        },

        fillPicture: async (slotId: string, mediaId: number, opts: { uses?: SlotUse[] } = {}) => {
          const slot = get().pictureMap.slots.find((x) => x.id === slotId);
          const { loadPictureSize } = await import('./assistantStore');
          const size = await loadPictureSize(mediaId);
          const args: Record<string, unknown> = { slot: slotId, mediaId };
          if (size && size.width > 0 && size.height > 0) args.size = { width: size.width, height: size.height };
          // A use as the step's schema takes it: the picture a place shows (`mediaId`) is read-only.
          if (opts.uses) args.uses = opts.uses.map(({ mediaId: _shown, ...use }) => use);
          const result = await get().applyOperations([{ name: 'fillPictureSlot', args, createdIds: [] }]);
          if (result.ok === false) return { ok: false, reason: result.reason, errors: result.errors };
          // The places the step wrote: the slot's sprite and background uses (or the listed ones).
          let places: number;
          if (slot) {
            places = slot.uses.filter((u) => u.kind !== 'line' && (!opts.uses || opts.uses.some((x) => sameUse(x, u)))).length;
          } else {
            const m = /in (\d+) place/.exec(result.summaries[0] ?? '');
            places = m ? Number(m[1]) : 0;
          }
          const after = get().pictureMap.slots.find((x) => x.id === slotId) ?? slot;
          return { ok: true, places, label: after ? pictureSlotLabel(after) : slotId };
        },

        discardPictures: (mediaIds: number[]) =>
          get().applyOperations([{ name: 'clearPictures', args: { mediaIds: [...new Set(mediaIds)] }, createdIds: [] }]),

        redo: async () => {
          const { history, story, batchPending } = get();
          const entry = history.future[history.future.length - 1];
          if (!entry || !story || groupDepth > 0 || batchPending) return;
          switch (entry.kind) {
            case 'edit':
              restore(entry.after);
              break;
            case 'batch':
              setPending(true);
              try {
                await redoBatch(entry);
              } finally {
                setPending(false);
              }
              return;
          }
          set((s) => {
            setHistory(s, {
              past: [...(s.history.past as StoryHistoryEntry[]), entry],
              future: (s.history.future as StoryHistoryEntry[]).slice(0, -1),
            });
          });
        },
      };

      async function applyOperations(steps: OperationStep[], opts: { turnId?: number } = {}): Promise<ApplyOperationsResult> {
        const { story, batchPending } = get();
        if (!story || batchPending) {
          return { ok: false, errors: [{ code: 'invalid_args', message: story ? 'a batch is already in flight' : 'no story is open' }], reason: 'failed' };
        }
        setPending(true);
        try {
          await flushOpenScene();
          // The server replays against the graph the author sees.
          if (!(await get().saveStory())) return { ok: false, errors: [], reason: 'network' };
          let res;
          try {
            res = await apiClient.applyStoryOperations(story.id, steps);
          } catch {
            return { ok: false, errors: [], reason: 'network' };
          }
          if (!res.success) {
            if (res.error === 'operations_failed') {
              return { ok: false, failedIndex: res.failedIndex, errors: res.errors ?? [], reason: 'failed' };
            }
            return { ok: false, errors: [], reason: 'network' };
          }
          if (get().story?.id !== story.id) return { ok: false, errors: [], reason: 'network' };
          const data = res.data;
          adoptStory(data.story);
          pushEntry({
            kind: 'batch',
            steps,
            revert: data.revert,
            summaries: data.summaries,
            createdStoryScenes: data.createdStoryScenes,
            ...(opts.turnId !== undefined ? { turnId: opts.turnId } : {}),
          });
          await refreshOpenScene(data.changedSceneIds, []);
          return { ok: true, summaries: data.summaries, createdSceneIds: data.createdSceneIds, changedSceneIds: data.changedSceneIds };
        } finally {
          setPending(false);
        }
      }

      async function linkScene(kind: 'reference' | 'copy' | 'own', sceneId: number) {
        const { story } = get();
        if (!story) return null;
        try {
          const res =
            kind === 'reference'
              ? await apiClient.addSceneReferenceToStory(story.id, sceneId)
              : kind === 'copy'
                ? await apiClient.copySceneToStory(story.id, sceneId)
                : await apiClient.ownSceneInStory(story.id, sceneId);
          if (res.success) {
            const ss = res.data as StorySceneWithScene;
            set((s) => {
              if (!s.story) return;
              s.story.storyScenes.push(ss);
              if (ss.choices && ss.choices.length > 0) {
                s.choiceStorySceneIds.add(ss.id);
                s.sceneChoicesMap.set(ss.id, ss.choices.map((c) => ({ ...c })));
              }
              revalidate(s);
            });
            return ss;
          }
          get().setNotice(res.error || 'Could not add the scene');
        } catch (err) {
          console.error(`Failed to ${kind} scene:`, err);
          get().setNotice('Could not add the scene');
        }
        return null;
      }
    }),
  ),
);

// Every sign-out (spec 001) drops the open story and any pending save.
registerSessionReset(() => useStoryStore.getState().reset());
